// ============================================
// スムーススクロール処理
// ============================================

/**
 * 定数
 */
const SCROLL_OFFSET = 0; // スクロール位置のオフセット（scroll-padding-topと同値）
const LOAD_TIMEOUT = 3000; // loadイベント待機の上限（ms）

/**
 * prefers-reduced-motionの設定に応じたスクロールbehaviorを返す
 * @returns {'smooth' | 'instant'}
 */
function getScrollBehavior() {
	const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
	return prefersReducedMotion.matches ? 'instant' : 'smooth';
}

/**
 * 指定要素までスクロールする
 * @param {HTMLElement} target - スクロール先の要素
 */
function scrollToTarget(target) {
	const rect = target.getBoundingClientRect();
	const targetTop = rect.top + window.scrollY - SCROLL_OFFSET;

	window.scrollTo({
		top: targetTop,
		behavior: getScrollBehavior(),
	});
}

/**
 * 全リソースの読み込み完了を待つ（タイムアウト付き）
 * @returns {Promise<void>}
 */
function waitForLoad() {
	// 既に読み込み完了している場合は即座に解決
	if (document.readyState === 'complete') {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		// loadイベント
		const onLoad = () => {
			clearTimeout(timerId);
			resolve();
		};

		// 安全タイムアウト
		const timerId = setTimeout(() => {
			window.removeEventListener('load', onLoad);
			resolve();
		}, LOAD_TIMEOUT);

		window.addEventListener('load', onLoad, { once: true });
	});
}

/**
 * requestAnimationFrameを2回ネストしてブラウザの再レイアウトを待つ
 * @returns {Promise<void>}
 */
function waitForRepaint() {
	return new Promise((resolve) => {
		requestAnimationFrame(() => {
			requestAnimationFrame(resolve);
		});
	});
}

/**
 * ページ読込時のハッシュ遷移を補正する
 */
async function handleInitialHash() {
	const hash = location.hash;
	if (!hash) return;

	const target = document.querySelector(hash);
	if (!target) return;

	// 全リソース読み込み完了を待つ
	await waitForLoad();

	// 再レイアウト完了を待つ
	await waitForRepaint();

	// 正しい位置へスクロール
	scrollToTarget(target);
}

/**
 * ページ内アンカーリンクのクリックを制御する
 */
function setupAnchorClickHandlers() {
	document.addEventListener('click', (e) => {
		// アンカーリンクを探す（クリック対象またはその親要素）
		const link = e.target.closest('a[href*="#"]');
		if (!link) return;

		// タブリンクは除外（tab.jsとの競合回避）
		if (link.getAttribute('role') === 'tab') return;

		const href = link.getAttribute('href');
		if (!href) return;

		// 別オリジンのリンクは除外
		if (link.origin !== location.origin) return;

		// 別ページへのリンクはブラウザデフォルトに任せる
		if (link.pathname !== location.pathname) return;

		// ハッシュ部分を取得
		const hash = link.hash;
		if (!hash) return;

		const target = document.querySelector(hash);
		if (!target) return;

		e.preventDefault();

		// スクロール実行
		scrollToTarget(target);

		// URLのハッシュを更新（履歴に反映）
		history.pushState(null, '', hash);
	});
}

/**
 * スムーススクロールを初期化
 */
export function initSmoothScroll() {
	// ページ内アンカークリックの制御
	setupAnchorClickHandlers();

	// ページ読込時のハッシュ遷移補正
	handleInitialHash();
}
